import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { replaceEqualDeep } from "../utils";

interface UseAsyncFetcherResult<T> {
  data: T;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  silentRefetch: () => Promise<void>;
}

export function useAsyncFetcher<T>(
  fetcher: () => Promise<T>,
  initialData: T,
  deps: unknown[],
  enabled = true,
  debounceMs = 0
): UseAsyncFetcherResult<T> {
  const [data, setData] = useState<T>(initialData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const prevDataRef = useRef<T>();

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const response = await fetcher();
      const newData = replaceEqualDeep(prevDataRef.current, response);
      if (newData !== prevDataRef.current) {
        prevDataRef.current = newData;
        setData(newData);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, deps);

  useEffect(() => {
    if (!enabled) return;
    if (debounceMs > 0) {
      const timer = setTimeout(fetchData, debounceMs);
      return () => clearTimeout(timer);
    }
    fetchData();
  }, [fetchData, enabled]);

  return useMemo(
    () => ({
      data,
      isLoading,
      isError: error !== null,
      error,
      refetch: () => fetchData(false),
      silentRefetch: () => fetchData(true),
    }),
    [data, isLoading, error, fetchData]
  );
}
